import { useState } from "react";
import { Form, useFormikContext } from "formik";
import { Formation } from "@/lib/Components/Form/Providers/Formation";
import { Dialog, DialogBody, DialogTitle } from "@/components/catalyst/dialog";
import { Button } from "@/components/catalyst/button";
import { TextInput } from "@/lib/Components/Form/Inputs/TextInput";
import { TextAreaInput } from "@/lib/Components/Form/Inputs/TextAreaInput";
import { CityInput } from "@/app/Cities/Cities/Components/CityInput";
import {
  blockMetadata,
  type BlockField,
  type BlockMeta,
} from "@/app/Blogs/Components/blockMetadata";

type EditingBlock = {
  name: string;
  props: Record<string, unknown>;
};

type Props = {
  open: boolean;
  onClose: (open: boolean) => void;
  onInsert: (name: string, props: Record<string, unknown>) => void;
  editing?: EditingBlock;
};

export function BlockInsertDialog({ open, onClose, onInsert, editing }: Props) {
  const [selectedName, setSelectedName] = useState<string | null>(null);

  const activeName = editing?.name ?? selectedName;
  const selected =
    activeName !== null && activeName !== undefined
      ? blockMetadata[activeName]
      : undefined;

  function handleClose(next: boolean) {
    if (!next) setSelectedName(null);
    onClose(next);
  }

  return (
    <Dialog open={open} onClose={handleClose} size="2xl">
      <DialogTitle>
        {selected
          ? editing
            ? `Edit ${selected.label}`
            : `Insert ${selected.label}`
          : "Insert a block"}
      </DialogTitle>
      <DialogBody>
        {selected && activeName ? (
          <BlockFieldForm
            meta={selected}
            initialProps={editing?.props}
            submitLabel={editing ? "Save" : "Insert"}
            onCancel={() => {
              if (editing) {
                onClose(false);
              } else {
                setSelectedName(null);
              }
            }}
            onSubmit={(props) => {
              onInsert(activeName, props);
              setSelectedName(null);
              onClose(false);
            }}
          />
        ) : (
          <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {Object.entries(blockMetadata).map(([name, meta]) => (
              <li key={name}>
                <button
                  type="button"
                  onClick={() => setSelectedName(name)}
                  className="block w-full rounded-md border border-zinc-200 p-3 text-left hover:border-blue-500 hover:bg-blue-50/40"
                >
                  <div className="text-sm font-semibold text-zinc-900">
                    {meta.label}
                  </div>
                  <div className="mt-1 text-xs text-zinc-500">
                    {meta.description}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </DialogBody>
    </Dialog>
  );
}

function BlockFieldForm({
  meta,
  initialProps,
  submitLabel,
  onSubmit,
  onCancel,
}: {
  meta: BlockMeta;
  initialProps?: Record<string, unknown>;
  submitLabel: string;
  onSubmit: (props: Record<string, unknown>) => void;
  onCancel: () => void;
}) {
  const initialValues = Object.fromEntries(
    meta.fields.map((f) => {
      const raw = initialProps?.[f.name];
      const value = typeof raw === "string" ? raw : null;
      return [f.name, value];
    }),
  );

  function isComplete(values: Record<string, string | null>) {
    return meta.fields
      .filter((f) => f.required)
      .every((f) => {
        const v = values[f.name];
        return typeof v === "string" && v.trim().length > 0;
      });
  }

  return (
    <Formation
      enableReinitialize
      initialEditingState
      initialValues={initialValues}
      onSubmit={(values) => {
        const props: Record<string, unknown> = {};
        for (const field of meta.fields) {
          const v = values[field.name];
          if (v !== null && v !== "") props[field.name] = v;
        }
        onSubmit(props);
      }}
    >
      <FormBody
        meta={meta}
        submitLabel={submitLabel}
        onCancel={onCancel}
        isComplete={isComplete}
      />
    </Formation>
  );
}

function FormBody({
  meta,
  submitLabel,
  onCancel,
  isComplete,
}: {
  meta: BlockMeta;
  submitLabel: string;
  onCancel: () => void;
  isComplete: (values: Record<string, string | null>) => boolean;
}) {
  const { values } = useFormikContext<Record<string, string | null>>();

  return (
    <Form className="space-y-4">
      <p className="text-sm text-zinc-500">{meta.description}</p>
      {meta.fields.length === 0 ? (
        <p className="text-sm text-zinc-600">
          This block has no configuration. Click {submitLabel.toLowerCase()} to
          continue.
        </p>
      ) : (
        meta.fields.map((field) => (
          <FieldRenderer key={field.name} field={field} />
        ))
      )}
      <div className="flex items-center justify-end gap-2 pt-2">
        <Button type="button" outline onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={!isComplete(values)}>
          {submitLabel}
        </Button>
      </div>
    </Form>
  );
}

function FieldRenderer({ field }: { field: BlockField }) {
  switch (field.type) {
    case "textarea":
      return <TextAreaInput name={field.name} label={field.label} />;
    case "city":
      return <CityInput name={field.name} label={field.label} />;
    default:
      return <TextInput name={field.name} label={field.label} />;
  }
}
